import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useThemeStore from '../store/themeStore';

const Footer = () => {
  const branding = useThemeStore(state => state.branding);
  const activeTheme = useThemeStore(state => state.activeTheme);
  const [showVendorLink, setShowVendorLink] = useState(false);

  useEffect(() => {
    const fetchMarketplaceSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/marketplace`);
        if (response.ok) {
          const data = await response.json();
          setShowVendorLink(data.vendor_mode_enabled && data.show_become_vendor);
        }
      } catch (error) {
        console.log('Marketplace settings not available');
      }
    };
    fetchMarketplaceSettings();
  }, []);
  
  return (
    <footer className="mt-auto" style={{ 
      background: `linear-gradient(135deg, ${activeTheme.colors.primary} 0%, ${activeTheme.colors.secondary} 50%, ${activeTheme.colors.tertiary} 100%)`
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1">
            <img 
              src="/navbar-logo.png?v=2026032611"
              alt={`${branding.shopName} Logo`}
              className="h-16 w-auto mb-4"
              onError={(e) => { e.currentTarget.src = branding.logo; }}
              data-testid="footer-logo"
            />
            <p className="text-sm text-white/90 mb-4">
              {branding.tagline}
            </p>
            
            {/* Social Media Icons */}
            <div className="flex gap-3">
              {branding.socialMedia?.facebook && (
                <a 
                  href={branding.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              
              {branding.socialMedia?.instagram && (
                <a 
                  href={branding.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              )}
              
              {branding.socialMedia?.tiktok && (
                <a 
                  href={branding.socialMedia.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3 className="font-semibold text-white mb-4">Shop</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/products?category=Dekoration" className="text-sm text-white/80 hover:text-white">
                  Dekoration
                </Link>
              </li>
              <li>
                <Link to="/products?category=Schmuck" className="text-sm text-white/80 hover:text-white">
                  Schmuck
                </Link>
              </li>
              <li>
                <Link to="/products?category=Accessoires" className="text-sm text-white/80 hover:text-white">
                  Accessoires
                </Link>
              </li>
            </ul>
          </div>

          {/* Kundenservice */}
          <div>
            <h3 className="font-semibold text-white mb-4">Kundenservice</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-sm text-white/80 hover:text-white" data-testid="footer-contact-link">
                  Kontakt
                </Link>
              </li>
              <li>
                <Link to="/versand" className="text-sm text-white/80 hover:text-white" data-testid="footer-shipping-link">
                  Versand & Zahlung
                </Link>
              </li>
              <li>
                <Link to="/newsletter" className="text-sm text-white/80 hover:text-white">
                  Newsletter
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-white/80 hover:text-white">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-white/80 hover:text-white" data-testid="footer-faq-link">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h3 className="font-semibold text-white mb-4">Rechtliches</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/agb" className="text-sm text-white/80 hover:text-white" data-testid="footer-agb-link">
                  AGB
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-sm text-white/80 hover:text-white" data-testid="footer-privacy-link">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/impressum" className="text-sm text-white/80 hover:text-white">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/retouren" className="text-sm text-white/80 hover:text-white">
                  Retouren & Rückgabe
                </Link>
              </li>
              {showVendorLink && (
                <li>
                  <Link to="/vendor-register" className="text-sm text-white/80 hover:text-white font-medium">
                    🏪 Verkäufer werden
                  </Link>
                </li>
              )}
              <li>
                <Link to="/widerruf" className="text-sm text-white/80 hover:text-white">
                  Widerrufsrecht
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/20 text-center">
          <p className="text-sm text-white/90">
            © {new Date().getFullYear()} {branding.shopName}. Alle Rechte vorbehalten.
          </p>
          <p className="text-xs text-white/70 mt-2">
            MwSt. wird nicht ausgewiesen (Kleinunternehmer, § 19 UStG)
          </p>
          <p className="text-xs text-white/70 mt-1" data-testid="footer-shipping-area">
            🇩🇪&nbsp;&nbsp;Versand derzeit ausschließlich innerhalb Deutschlands
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;